import { useMemo } from "react";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { errorMessage, useMonitoringLive, useMonitoringMetrics } from "../api";
import { EmptyBlock, ErrorBlock, FetchingDot, LoadingBlock } from "../components/primitives";
import { SubHeading } from "./backend/stat-tile";
import { ManualPods } from "./pods/manual-pods";
import { PodCard } from "./pods/pod-card";
import { PodWindowCharts } from "./pods/pod-charts";
import { PodGpuCharts } from "./pods/pod-gpu-charts";
import { podOrder } from "./pods/pod-order";
import { PodTotals } from "./pods/pod-totals";
import { PoolStrip } from "./pods/pool-strip";

/**
 * GPU pods: the live pool (every 5 s) with one card per pod, then the window's history per outcome,
 * latency, pod, queue and health, the window totals per pod, and the pods' own GPU and event loop.
 */
export function PodsTab() {
  const { t } = useLocalization();
  const live = useMonitoringLive();
  const metrics = useMonitoringMetrics();

  const section = live.data?.pods;
  const order = useMemo(() => podOrder(section?.pods, metrics.data), [section?.pods, metrics.data]);
  const index = useMemo(() => new Map(order.map((endpoint, i) => [endpoint, i])), [order]);
  const liveSet = useMemo(() => new Set((section?.pods ?? []).map((pod) => pod.endpoint)), [section?.pods]);
  const windowLabel = metrics.data ? t(`monitoring.window.long.${metrics.data.window}` as TranslationKey) : "";

  return (
    <div className="flex min-w-0 flex-col gap-6">
      {/* live pool ------------------------------------------------------------------------------- */}
      {live.isPending ? (
        <div className="flex flex-col gap-4">
          <LoadingBlock height={150} />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
            <LoadingBlock height={420} />
            <LoadingBlock height={420} className="hidden md:block" />
          </div>
        </div>
      ) : live.isError && !section ? (
        <ErrorBlock message={errorMessage(live.error)} onRetry={() => void live.refetch()} />
      ) : section ? (
        <>
          {live.isError && <ErrorBlock message={errorMessage(live.error)} onRetry={() => void live.refetch()} />}
          <PoolStrip section={section} generatedAt={live.data?.generated_at ?? null} fetching={live.isFetching} />
          <ManualPods section={section} />
          <div className="flex flex-col gap-3">
            <SubHeading>
              {t("monitoring.pods.cards.title")} · {section.pods.length}
            </SubHeading>
            {section.pods.length === 0 ? (
              <EmptyBlock>
                {section.pool ? t("monitoring.pods.cards.empty") : t("monitoring.pods.cards.noPool")}
              </EmptyBlock>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
                {section.pods.map((pod) => (
                  <PodCard
                    key={pod.endpoint}
                    pod={pod}
                    index={index.get(pod.endpoint) ?? 0}
                    pool={section.pool}
                    now={live.data?.generated_at ?? Date.now()}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}

      {/* window history ------------------------------------------------------------------------- */}
      <div className="flex flex-col gap-3">
        <SubHeading actions={<FetchingDot active={metrics.isFetching} />}>
          {t("monitoring.pods.window.title", { window: windowLabel })}
        </SubHeading>
        {metrics.isPending ? (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <LoadingBlock key={i} height={270} />
            ))}
          </div>
        ) : metrics.isError && !metrics.data ? (
          <ErrorBlock message={errorMessage(metrics.error)} onRetry={() => void metrics.refetch()} />
        ) : metrics.data ? (
          <>
            {metrics.isError && <ErrorBlock message={errorMessage(metrics.error)} onRetry={() => void metrics.refetch()} />}
            <PodWindowCharts metrics={metrics.data} order={order} pool={section?.pool ?? null} />
            <PodTotals metrics={metrics.data} order={order} live={liveSet} />
            <div className="mt-3 flex flex-col gap-3">
              <SubHeading>{t("monitoring.pods.window.gpu", { window: windowLabel })}</SubHeading>
              <PodGpuCharts metrics={metrics.data} order={order} />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
